/** Strings of `/work-items`, the `/work-items/new/:type` form and the floating timer widget: `wiNewEn` is the source, `wiNewCs` must have the same keys. */
export const wiNewEn = {
  wiIndex: {
    title: "Work Items",
    total: "{{count}} work items total",
    searchPlaceholder: "Search work items...",
    unnamed: "(unnamed)",
    types: { feature: "Feature", bug: "Bug", standalone: "Task", hotfix: "Hotfix" },
    priorities: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
  },
  wiNew: {
    cancel: "Cancel",
    project: "Project",
    selectProject: "— select a project —",
    noProjects: "No projects",
    initialState: "Initial state",
    stateNew: "New (default)",
    stateEstimation: "Estimation (requires assignee)",
    stateApproving: "Approving (requires estimate)",
    priority: "Priority",
    priorities: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
    sla: "SLA: urgent {{urgent}} · high {{high}} · normal {{normal}} · low {{low}}",
    assignee: "Assignee",
    defaultTeam: "— default team (Metoděj develops) —",
    selectProjectFirst: "Select a project first…",
    noMembers: "No project members",
    targetRelease: "Target release",
    none: "— none —",
    noReleases: "No releases",
    title: "Title",
    description: "Description",
    estimatedHours: "Estimated hours",
    dueDate: "Due date",
    creating: "Creating…",
    requiredError: "Project and title are required.",
    createFailedApi:
      "Couldn't create the work item — check the fields and your project membership.",
    createFailed: "Couldn't create the work item.",
    types: {
      feature: {
        new: "New Feature",
        hint: "New functionality — full feature workflow",
        details: "Feature details",
        titlePlaceholder: "Feature title",
        describe: "Describe the feature…",
        create: "Create Feature",
      },
      bug: {
        new: "New Bug",
        hint: "A defect or error — runs the same full workflow as a feature",
        details: "Bug details",
        titlePlaceholder: "Bug title",
        describe: "Describe the bug…",
        create: "Create Bug",
      },
      standalone: {
        new: "New Task",
        hint: "No-code-work ticket — runs the lightweight Task workflow",
        details: "Task details",
        titlePlaceholder: "Task title",
        describe: "Describe the task…",
        create: "Create Task",
      },
      hotfix: {
        new: "New Hotfix",
        hint: "Must ship RIGHT NOW, bypassing the normal dev/test chain — rare by design. For an everyday bug, use Bug instead.",
        details: "Hotfix details",
        titlePlaceholder: "Hotfix title",
        describe: "Describe the hotfix…",
        create: "Create Hotfix",
      },
    },
  },
  wiTimer: {
    startAria: "Start timer",
    startTimer: "Start Timer",
    stopTimer: "Stop Timer",
    project: "Project",
    target: "Work item or overhead",
    descriptionOptional: "Description (optional)",
    descriptionPlaceholder: "What are you working on?",
    loading: "Loading…",
    selectProject: "— select project —",
    noProjects: "No projects",
    selectProjectFirst: "Select a project first",
    pickTarget: "— pick work item or overhead —",
    noOptions: "No options",
    hintWorkItem: "Work item",
    hintOverhead: "Overhead",
    overheadDefault: "{{name}} (default)",
    overheadTitle: "Overhead: {{name}}",
    cancel: "Cancel",
    start: "Start",
    elapsed: "Elapsed",
    startTime: "Start time",
    timerStartAria: "Timer start",
    apply: "Apply changes (keep running)",
    stopHint: "Stopping will create a time entry with the elapsed duration.",
    discard: "Discard",
    keepRunning: "Keep running",
    saving: "Saving…",
    stopSave: "Stop & save",
    startFailedStatus: "Start timer failed ({{status}})",
    startFailed: "Start timer failed",
    invalidStart: "Invalid start time",
    updated: "Timer updated",
    updateFailed: "Timer update failed",
    error: "Error",
    newFeature: "New Feature",
    newBug: "New Bug",
    newTask: "New Task",
    newHotfix: "New Hotfix",
    newIdea: "New Idea",
  },
};

export const wiNewCs: typeof wiNewEn = {
  wiIndex: {
    title: "Tikety",
    total: "Celkem tiketů: {{count}}",
    searchPlaceholder: "Hledat tikety...",
    unnamed: "(bez jména)",
    types: { feature: "Funkce", bug: "Chyba", standalone: "Úkol", hotfix: "Hotfix" },
    priorities: { low: "Nízká", normal: "Normální", high: "Vysoká", urgent: "Naléhavá" },
  },
  wiNew: {
    cancel: "Zrušit",
    project: "Projekt",
    selectProject: "— vyberte projekt —",
    noProjects: "Žádné projekty",
    initialState: "Počáteční stav",
    stateNew: "Nový (výchozí)",
    stateEstimation: "Odhad (vyžaduje řešitele)",
    stateApproving: "Schvalování (vyžaduje odhad)",
    priority: "Priorita",
    priorities: { low: "Nízká", normal: "Normální", high: "Vysoká", urgent: "Naléhavá" },
    sla: "SLA: naléhavá {{urgent}} · vysoká {{high}} · normální {{normal}} · nízká {{low}}",
    assignee: "Řešitel",
    defaultTeam: "— výchozí tým (vyvíjí Metoděj) —",
    selectProjectFirst: "Nejprve vyberte projekt…",
    noMembers: "Žádní členové projektu",
    targetRelease: "Cílové vydání",
    none: "— žádné —",
    noReleases: "Žádná vydání",
    title: "Název",
    description: "Popis",
    estimatedHours: "Odhad v hodinách",
    dueDate: "Termín",
    creating: "Vytváří se…",
    requiredError: "Projekt a název jsou povinné.",
    createFailedApi: "Tiket se nepodařilo vytvořit — zkontrolujte pole a členství v projektu.",
    createFailed: "Tiket se nepodařilo vytvořit.",
    types: {
      feature: {
        new: "Nová funkce",
        hint: "Nová funkcionalita — úplný workflow funkce",
        details: "Údaje o funkci",
        titlePlaceholder: "Název funkce",
        describe: "Popište funkci…",
        create: "Vytvořit funkci",
      },
      bug: {
        new: "Nová chyba",
        hint: "Vada nebo chyba — prochází stejným úplným workflow jako funkce",
        details: "Údaje o chybě",
        titlePlaceholder: "Název chyby",
        describe: "Popište chybu…",
        create: "Vytvořit chybu",
      },
      standalone: {
        new: "Nový úkol",
        hint: "Tiket bez programátorské práce — prochází odlehčeným workflow úkolu",
        details: "Údaje o úkolu",
        titlePlaceholder: "Název úkolu",
        describe: "Popište úkol…",
        create: "Vytvořit úkol",
      },
      hotfix: {
        new: "Nový hotfix",
        hint: "Musí jít ven HNED, mimo běžný řetězec dev/test — ze zásady výjimečně. Pro běžnou chybu použijte Chybu.",
        details: "Údaje o hotfixu",
        titlePlaceholder: "Název hotfixu",
        describe: "Popište hotfix…",
        create: "Vytvořit hotfix",
      },
    },
  },
  wiTimer: {
    startAria: "Spustit časovač",
    startTimer: "Spustit časovač",
    stopTimer: "Zastavit časovač",
    project: "Projekt",
    target: "Tiket nebo režie",
    descriptionOptional: "Popis (nepovinný)",
    descriptionPlaceholder: "Na čem pracujete?",
    loading: "Načítání…",
    selectProject: "— vyberte projekt —",
    noProjects: "Žádné projekty",
    selectProjectFirst: "Nejprve vyberte projekt",
    pickTarget: "— vyberte tiket nebo režii —",
    noOptions: "Žádné možnosti",
    hintWorkItem: "Tiket",
    hintOverhead: "Režie",
    overheadDefault: "{{name}} (výchozí)",
    overheadTitle: "Režie: {{name}}",
    cancel: "Zrušit",
    start: "Spustit",
    elapsed: "Uplynulo",
    startTime: "Čas zahájení",
    timerStartAria: "Zahájení časovače",
    apply: "Použít změny (nechat běžet)",
    stopHint: "Zastavením se vytvoří časový záznam s uplynulou dobou.",
    discard: "Zahodit",
    keepRunning: "Nechat běžet",
    saving: "Ukládání…",
    stopSave: "Zastavit a uložit",
    startFailedStatus: "Spuštění časovače selhalo ({{status}})",
    startFailed: "Spuštění časovače selhalo",
    invalidStart: "Neplatný čas zahájení",
    updated: "Časovač upraven",
    updateFailed: "Úprava časovače selhala",
    error: "Chyba",
    newFeature: "Nová funkce",
    newBug: "Nová chyba",
    newTask: "Nový úkol",
    newHotfix: "Nový hotfix",
    newIdea: "Nový nápad",
  },
};
